import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const AdminExport = () => {
  const [exportType, setExportType] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);
  const pollingRef = useRef(null);

  const downloadExportFile = async (type, currentJobId) => {
    const endpoint = type === 'excel'
      ? `/admin/export/download/excel/${currentJobId}`
      : `/admin/export/download/pdf/${currentJobId}`;

    const extension = type === 'excel' ? 'xlsx' : 'pdf';
    const mimeType = type === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/pdf';

    const response = await api.get(endpoint, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `astro_export_${new Date().toISOString().split('T')[0]}.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Poll for progress
  useEffect(() => {
    if (jobId) {
      pollingRef.current = setInterval(async () => {
        try {
          const response = await api.get(`/admin/export/progress/${jobId}`);
          setProgress(response.data);

          if (response.data.status === 'completed') {
            clearInterval(pollingRef.current);
            try {
              await downloadExportFile(exportType, jobId);
              setLoading(false);
            } catch (downloadErr) {
              console.error('Download failed:', downloadErr);
              setError('Export completed, but download failed. Please try again.');
              setLoading(false);
            }
          } else if (response.data.status === 'error') {
            clearInterval(pollingRef.current);
            setLoading(false);
            setError(response.data.error || 'Export failed');
          }
        } catch (err) {
          console.error('Error polling progress:', err);
        }
      }, 1000);

      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [jobId, exportType]);

  const handleExport = async () => {
    setLoading(true);
    setError('');
    setEmailStatus('');
    setProgress(null);
    setJobId(null);

    try {
      // Backend generates both PDF and Excel in one export job
      const response = await api.post('/admin/export/start');
      setJobId(response.data.job_id);
      setProgress({
        status: 'processing',
        progress: 0,
        current_task: 'Initializing...',
        total_doctors: 0,
        processed_doctors: 0,
      });
    } catch (err) {
      const message = err.response?.data?.detail || 'Export failed. Please try again.';
      setError(message);
      setLoading(false);
    }
  };

  const cancelExport = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    setLoading(false);
    setJobId(null);
    setProgress(null);
  };

  const handleSendEmail = async () => {
    if (!jobId) {
      setError('No completed export job found to email.');
      return;
    }

    setEmailLoading(true);
    setEmailStatus('');
    setError('');

    try {
      const response = await api.post(`/admin/export/send-email/${jobId}`);
      setEmailStatus(response.data?.message || 'Report sent successfully.');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send report email.';
      setError(message);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="container admin-container" style={{ maxWidth: '800px' }}>
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="page-title">Export Data</h1>
          <p className="page-subtitle">Export doctor profiles as PDF or Excel</p>
        </div>
        <Link to="/admin" className="btn btn-secondary" style={{ width: 'auto', padding: '10px 20px' }}>
          ← Back
        </Link>
      </div>

      {/* Export Options */}
      <div className="card">
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '20px', color: '#333' }}>
          Select Export Type
        </h3>

        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
          <label
            style={{
              flex: 1,
              padding: '20px',
              border: `2px solid ${exportType === 'pdf' ? '#1f84f1' : '#e0e0e0'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backgroundColor: exportType === 'pdf' ? '#f0f7ff' : '#fff',
            }}
          >
            <input
              type="radio"
              name="exportType"
              value="pdf"
              checked={exportType === 'pdf'}
              onChange={(e) => setExportType(e.target.value)}
              style={{ display: 'none' }}
            />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>PDF Export</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                All posters with serial numbers (1-1000)
              </div>
            </div>
          </label>

          <label
            style={{
              flex: 1,
              padding: '20px',
              border: `2px solid ${exportType === 'excel' ? '#1f84f1' : '#e0e0e0'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              backgroundColor: exportType === 'excel' ? '#f0f7ff' : '#fff',
            }}
          >
            <input
              type="radio"
              name="exportType"
              value="excel"
              checked={exportType === 'excel'}
              onChange={(e) => setExportType(e.target.value)}
              style={{ display: 'none' }}
            />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📊</div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>Excel Export</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Doctor details with MR info
              </div>
            </div>
          </label>
        </div>

        {/* Export details */}
        {exportType === 'pdf' && (
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
              PDF Export Details:
            </h4>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px', 
              fontSize: '14px', 
              color: '#555',
              lineHeight: '1.8'
            }}>
              <li>All doctor posters combined in a single PDF</li>
              <li>Serial numbers (1-1000) added at bottom-right of each page</li>
              <li>MRs sorted by employee code</li>
              <li>Doctors grouped per MR</li>
              <li>Full resolution (2401 × 3480 px)</li>
            </ul>
          </div>
        )}

        {exportType === 'excel' && (
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
              Excel Export Columns:
            </h4>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '20px', 
              fontSize: '14px', 
              color: '#555',
              lineHeight: '1.8'
            }}>
              <li>Serial Number</li>
              <li>Doctor Name</li>
              <li>Date of Birth</li>
              <li>Birth City</li>
              <li>Birth Time</li>
              <li>MR Name</li>
              <li>MR Employee Code</li>
              <li>MR HQ</li>
              <li>MR Zone</li>
              <li>MR Region</li>
              <li>MR Level</li>
            </ul>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}
        {emailStatus && <p style={{ color: '#198754', fontWeight: 500 }}>{emailStatus}</p>}

        {/* Progress bar for PDF */}
        {loading && progress && exportType === 'pdf' && (
          <div style={{ marginBottom: '24px' }}>
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ width: `${progress.progress || 0}%` }}
              ></div>
            </div>
            <div className="progress-text">
              {progress.status === 'processing' && (
                <>
                  {progress.current_task || 'Processing...'}
                  {' '}({progress.processed_doctors || 0}/{progress.total_doctors || '?'})
                </>
              )}
              {progress.status === 'completed' && 'Export completed! Downloading...'}
              {progress.status === 'error' && `Export failed: ${progress.error}`}
            </div>
          </div>
        )}

        {/* Export button */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleExport}
            className="btn btn-primary"
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ marginRight: '8px', width: '20px', height: '20px' }}></span>
                {exportType === 'pdf' ? 'Generating PDF...' : 'Exporting...'}
              </>
            ) : (
              `📥 Export as ${exportType.toUpperCase()}`
            )}
          </button>

          {loading && exportType === 'pdf' && (
            <button
              onClick={cancelExport}
              className="btn btn-secondary"
              style={{ width: 'auto' }}
            >
              Cancel
            </button>
          )}
        </div>

        {!loading && progress?.status === 'completed' && (
          <button
            onClick={handleSendEmail}
            className="btn btn-secondary"
            disabled={emailLoading}
            style={{ marginTop: '12px' }}
          >
            {emailLoading ? 'Sending Report Email...' : '✉️ Send Report to bala.subramani@pitchman.in'}
          </button>
        )}
      </div>

      {/* Info card */}
      <div className="card" style={{ marginTop: '24px', backgroundColor: '#fff8e6' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '24px', marginRight: '16px' }}>💡</span>
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Export Tips
            </h4>
            <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: '1.6' }}>
              PDF export may take several minutes depending on the number of doctors. 
              The progress bar will show real-time status. You can cancel the export at any time.
              Excel export is faster and recommended for data analysis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminExport;
